import React from "react";

const MAX_PAIRS = 15;

const createPair = (index) => ({
  id: `pair-${index + 1}`,
  imageUrl: "",
  description: "",
});

const clampPairCount = (value) => {
  const num = Number(value);
  if (!Number.isInteger(num)) return 1;
  if (num < 1) return 1;
  if (num > MAX_PAIRS) return MAX_PAIRS;
  return num;
};

const MemoryContentStep = ({ formData, updateFormData, fieldErrors = {} }) => {
  const settings =
    formData?.gameData?.settings &&
    typeof formData.gameData.settings === "object"
      ? formData.gameData.settings
      : {};
  const pairs = Array.isArray(formData?.gameData?.pairs)
    ? formData.gameData.pairs
    : [];
  const pairCount = clampPairCount(settings.pairCount || pairs.length || 1);

  const updateMemoryData = (nextSettings, nextPairs) => {
    updateFormData("gameData", {
      pairs: nextPairs,
      settings: nextSettings,
    });
  };

  const handlePairCountChange = (rawValue) => {
    const nextCount = clampPairCount(rawValue);
    const nextPairs = [...pairs];
    if (nextPairs.length < nextCount) {
      for (let i = nextPairs.length; i < nextCount; i += 1) {
        nextPairs.push(createPair(i));
      }
    } else if (nextPairs.length > nextCount) {
      nextPairs.length = nextCount;
    }

    updateMemoryData(
      {
        ...settings,
        pairCount: nextCount,
      },
      nextPairs.map((pair, index) => ({
        id: pair?.id || `pair-${index + 1}`,
        imageUrl: pair?.imageUrl || "",
        description: pair?.description || pair?.message || "",
      }))
    );
  };

  const handlePairImageChange = (index, imageUrl) => {
    const nextPairs = [...pairs];
    nextPairs[index] = {
      id: nextPairs[index]?.id || `pair-${index + 1}`,
      imageUrl,
      description: nextPairs[index]?.description || nextPairs[index]?.message || "",
    };
    updateMemoryData({ ...settings, pairCount }, nextPairs);
  };

  const handlePairDescriptionChange = (index, description) => {
    const nextPairs = [...pairs];
    nextPairs[index] = {
      id: nextPairs[index]?.id || `pair-${index + 1}`,
      imageUrl: nextPairs[index]?.imageUrl || "",
      description,
    };
    updateMemoryData({ ...settings, pairCount }, nextPairs);
  };

  const handleTimerToggle = (enabled) => {
    updateMemoryData(
      {
        ...settings,
        pairCount,
        timerSeconds: enabled
          ? settings.timerSeconds && Number(settings.timerSeconds) > 0
            ? Number(settings.timerSeconds)
            : 60
          : null,
      },
      pairs
    );
  };

  const handleTimerChange = (value) => {
    updateMemoryData(
      {
        ...settings,
        pairCount,
        timerSeconds: value === "" ? null : Math.max(0, Number(value)),
      },
      pairs
    );
  };

  const timerEnabled =
    settings.timerSeconds !== null && settings.timerSeconds !== undefined;
  const showMatchSuccessPopup = Boolean(settings.showMatchSuccessPopup);

  const handleMatchSuccessPopupToggle = (enabled) => {
    updateMemoryData(
      {
        ...settings,
        pairCount,
        showMatchSuccessPopup: enabled,
        memoryMatchSuccessMessage: enabled
          ? settings.memoryMatchSuccessMessage || ""
          : "",
      },
      pairs
    );
  };

  const handleMatchSuccessMessageChange = (value) => {
    updateMemoryData(
      {
        ...settings,
        pairCount,
        memoryMatchSuccessMessage: value,
      },
      pairs
    );
  };

  return (
    <div className="step-content">
      <div className="form-group">
        <label>NUMBER OF PAIRS (MAX 15)</label>
        <input
          type="number"
          min="1"
          max={MAX_PAIRS}
          value={pairCount}
          onChange={(e) => handlePairCountChange(e.target.value)}
        />
      </div>

      <div className="form-group">
        <label>CARD BACKSIDE IMAGE URL</label>
        <input
          type="url"
          value={settings.cardBackImageUrl || ""}
          onChange={(e) =>
            updateMemoryData(
              { ...settings, pairCount, cardBackImageUrl: e.target.value },
              pairs
            )
          }
          placeholder="https://example.com/card-back.jpg"
          className={fieldErrors.memoryBackImage ? "error" : ""}
        />
        {fieldErrors.memoryBackImage && (
          <div className="field-error">{fieldErrors.memoryBackImage}</div>
        )}
      </div>

      <div className="form-group">
        <label>TIMER (seconds)</label>
        <div className="toggle-group" style={{ display: "flex", gap: "1rem" }}>
          <label
            style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}
          >
            <input
              type="radio"
              name="memoryTimerMode"
              checked={!timerEnabled}
              onChange={() => handleTimerToggle(false)}
            />
            No timer
          </label>
          <label
            style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}
          >
            <input
              type="radio"
              name="memoryTimerMode"
              checked={timerEnabled}
              onChange={() => handleTimerToggle(true)}
            />
            Set timer
          </label>
        </div>
        {timerEnabled ? (
          <input
            type="number"
            min="0"
            value={settings.timerSeconds ?? 0}
            onChange={(e) => handleTimerChange(e.target.value)}
            placeholder="Seconds"
            style={{ marginTop: "0.75rem" }}
          />
        ) : null}
      </div>

      <div className="form-group">
        <label>PAIR IMAGE URLS</label>
        <div className="memory-pairs-grid">
          {Array.from({ length: pairCount }).map((_, index) => (
            <div key={`pair-input-${index}`} className="memory-pair-input">
              <label>{`PAIR ${index + 1} IMAGE`}</label>
              <input
                type="url"
                value={pairs[index]?.imageUrl || ""}
                onChange={(e) => handlePairImageChange(index, e.target.value)}
                placeholder={`https://example.com/pair-${index + 1}.jpg`}
                className={fieldErrors.memoryPairs ? "error" : ""}
              />
              <label>{`PAIR ${index + 1} MATCH DESCRIPTION (OPTIONAL)`}</label>
              <input
                type="text"
                value={pairs[index]?.description || pairs[index]?.message || ""}
                onChange={(e) =>
                  handlePairDescriptionChange(index, e.target.value)
                }
                placeholder="Description shown when this pair image is pressed"
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: "1rem" }}>
          <label>SHOW POPUP ON SUCCESSFUL PAIR</label>
          <div className="toggle-group" style={{ display: "flex", gap: "1rem" }}>
            <label
              style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}
            >
              <input
                type="radio"
                name="memorySuccessPopupMode"
                checked={!showMatchSuccessPopup}
                onChange={() => handleMatchSuccessPopupToggle(false)}
              />
              No
            </label>
            <label
              style={{ display: "flex", gap: "0.35rem", alignItems: "center" }}
            >
              <input
                type="radio"
                name="memorySuccessPopupMode"
                checked={showMatchSuccessPopup}
                onChange={() => handleMatchSuccessPopupToggle(true)}
              />
              Yes
            </label>
          </div>
        </div>
        {showMatchSuccessPopup ? (
          <div style={{ marginTop: "0.75rem" }}>
            <label>MEMORY MATCH SUCCESS MESSAGE</label>
            <input
              type="text"
              value={settings.memoryMatchSuccessMessage || ""}
              onChange={(e) => handleMatchSuccessMessageChange(e.target.value)}
              placeholder="Message shown in celebration popup on every successful pair"
            />
          </div>
        ) : null}
        {fieldErrors.memoryPairs && (
          <div className="field-error">{fieldErrors.memoryPairs}</div>
        )}
      </div>
    </div>
  );
};

export default MemoryContentStep;
